/**
 * Phase 2b · Credit Policy CRUD + Committee Approval Console.
 *
 * Feature-flag gated on the backend (`/admin/credit-policies` returns 404
 * outside Preview). The UI simply attempts a list call — if it 404s the
 * user sees the disabled banner. No customer-facing surface. No live use.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminCreditPolicies } from "../lib/api";
import { PolicyControlCenter } from "@/components/admin/PolicyControlCenter";

const STATUS_BADGE = {
  draft: "bg-neutral-500/15 text-neutral-200 border-neutral-500/40",
  pending_committee_approval: "bg-amber-500/15 text-amber-200 border-amber-500/40",
  active: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40",
  retired: "bg-slate-500/15 text-slate-200 border-slate-500/40",
  rejected: "bg-rose-500/15 text-rose-200 border-rose-500/40",
};

const PRODUCTS = [
  { v: "personal_loan", label: "Personal Loan" },
  { v: "business_loan", label: "Business Loan" },
  { v: "home_loan", label: "Home Loan" },
  { v: "loan_against_property", label: "Loan Against Property" },
];

const StatusChip = ({ status }) => (
  <span
    data-testid={`policy-status-${status}`}
    className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
      STATUS_BADGE[status] || STATUS_BADGE.draft
    }`}
  >
    {status?.replace(/_/g, " ")}
  </span>
);

// ─── Draft-editor modal ─────────────────────────────────────────
function DraftEditor({ policy, onClose, onSaved }) {
  const isNew = !policy;
  const [form, setForm] = useState({
    product: policy?.product || "personal_loan",
    min_age: policy?.min_age ?? 21,
    max_age: policy?.max_age ?? 60,
    min_verified_monthly_income: policy?.min_verified_monthly_income ?? "",
    max_foir: policy?.max_foir ?? "0.5",
    min_tenure_months: policy?.min_tenure_months ?? 6,
    max_tenure_months: policy?.max_tenure_months ?? 60,
    eligibility_multiplier: policy?.eligibility_multiplier ?? "",
    min_bank_history_months: policy?.min_bank_history_months ?? 6,
    product_cap: policy?.product_cap ?? "",
    audit_note: policy?.audit_note || "",
    reason: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  async function submit(e) {
    e?.preventDefault?.();
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      if (isNew) {
        const body = { ...form, product_cap: form.product_cap || null };
        await AdminCreditPolicies.createDraft(body);
      } else {
        const patch = {};
        for (const k of ["min_age", "max_age", "min_verified_monthly_income",
                         "max_foir", "min_tenure_months", "max_tenure_months",
                         "eligibility_multiplier", "min_bank_history_months",
                         "product_cap", "audit_note"]) {
          if (String(form[k]) !== String(policy?.[k] ?? "")) patch[k] = form[k];
        }
        if (Object.keys(patch).length === 0) {
          setErr("No changes to save.");
          setBusy(false);
          return;
        }
        await AdminCreditPolicies.updateDraft(policy.id, patch, form.reason);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail?.message || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
         data-testid="policy-editor-modal">
      <form onSubmit={submit}
            className="w-full max-w-3xl rounded-xl border border-white/10 bg-[#0b0f1a] p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-semibold text-amber-300">
            {isNew ? "New Draft Policy" : `Edit Draft · v${policy.version}`}
          </h2>
          <button type="button" onClick={onClose} className="text-white/60 hover:text-white">
            ✕
          </button>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {isNew && (
            <label className="text-xs text-neutral-400">
              Product
              <select data-testid="policy-input-product"
                      className="mt-1 w-full rounded border border-white/10 bg-black/40 px-2 py-1 text-sm text-white"
                      value={form.product} onChange={set("product")}>
                {PRODUCTS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
              </select>
            </label>
          )}
          {[
            ["min_age", "Min Age"], ["max_age", "Max Age"],
            ["min_verified_monthly_income", "Min Verified Monthly Income (₹)"],
            ["max_foir", "Max FOIR (0.0-1.0)"],
            ["min_tenure_months", "Min Tenure (months)"], ["max_tenure_months", "Max Tenure (months)"],
            ["eligibility_multiplier", "Eligibility Multiplier"],
            ["min_bank_history_months", "Min Bank History (months)"],
            ["product_cap", "Product Cap (₹, optional)"],
          ].map(([k, label]) => (
            <label key={k} className="text-xs text-neutral-400">
              {label}
              <input data-testid={`policy-input-${k}`}
                     className="mt-1 w-full rounded border border-white/10 bg-black/40 px-2 py-1 text-sm text-white"
                     value={form[k]} onChange={set(k)}
                     placeholder={k === "min_verified_monthly_income" || k === "eligibility_multiplier"
                                  ? "Committee sets this — no placeholders" : ""} />
            </label>
          ))}
          <label className="col-span-full text-xs text-neutral-400">
            Audit Note (visible in every audit row)
            <input data-testid="policy-input-audit-note"
                   className="mt-1 w-full rounded border border-white/10 bg-black/40 px-2 py-1 text-sm text-white"
                   value={form.audit_note} onChange={set("audit_note")} />
          </label>
          <label className="col-span-full text-xs text-neutral-400">
            Reason for this action (mandatory · min 8 chars)
            <input data-testid="policy-input-reason"
                   className="mt-1 w-full rounded border border-white/10 bg-black/40 px-2 py-1 text-sm text-white"
                   value={form.reason} onChange={set("reason")}
                   required minLength={8} />
          </label>
        </div>
        {err && <p data-testid="policy-editor-error" className="mt-3 rounded border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{err}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="rounded border border-white/10 px-3 py-1 text-xs text-neutral-300">
            Cancel
          </button>
          <button data-testid="policy-editor-save"
                  disabled={busy || (form.reason.length < 8)}
                  className="rounded bg-amber-400 px-3 py-1 text-xs font-semibold text-black disabled:opacity-50">
            {busy ? "Saving…" : isNew ? "Create Draft" : "Save Draft"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Reason-modal for state-transition actions ─────────────────
function ReasonPrompt({ title, defaultValue = "", onCancel, onConfirm, busy, summary = null }) {
  const [reason, setReason] = useState(defaultValue);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
         data-testid="policy-reason-prompt">
      <div className="w-full max-w-md rounded-xl border border-white/10 bg-[#0b0f1a] p-5 shadow-2xl">
        <h3 className="mb-3 text-sm font-semibold text-amber-300">{title}</h3>
        {summary}
        <textarea data-testid="policy-reason-input"
                  className="w-full rounded border border-white/10 bg-black/40 px-2 py-1 text-sm text-white"
                  rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                  minLength={8} placeholder="Provide a reason (min 8 chars)…" />
        <div className="mt-3 flex justify-end gap-2">
          <button onClick={onCancel} className="rounded border border-white/10 px-3 py-1 text-xs text-neutral-300">
            Cancel
          </button>
          <button data-testid="policy-reason-confirm"
                  disabled={busy || reason.length < 8}
                  onClick={() => onConfirm(reason)}
                  className="rounded bg-amber-400 px-3 py-1 text-xs font-semibold text-black disabled:opacity-50">
            {busy ? "Working…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminCreditPoliciesPage() {
  const [state, setState] = useState({ loading: true, error: null, items: [], flags: {} });
  const [editing, setEditing] = useState(null); // null | "new" | policy-object
  const [prompt, setPrompt] = useState(null); // {title, onConfirm}
  const [busy, setBusy] = useState(false);

  async function load() {
    setState((s) => ({ ...s, loading: true }));
    try {
      const j = await AdminCreditPolicies.list({ limit: 200 });
      setState({ loading: false, error: null, items: j.items, flags: j.flags });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setState({ loading: false, items: [], flags: {},
                 error: typeof detail === "object" ? detail.message : detail || e.message });
    }
  }
  useEffect(() => { load(); }, []);

  const doTransition = (title, fn, summary = null) => setPrompt({
    title, summary,
    onConfirm: async (reason) => {
      setBusy(true);
      try {
        await fn(reason);
        setPrompt(null);
        await load();
      } catch (e) {
        alert(e?.response?.data?.detail?.message || e.message);
      } finally {
        setBusy(false);
      }
    },
  });

  const submitPolicy = (id) => doTransition("Submit for committee approval",
    (reason) => AdminCreditPolicies.submit(id, reason));
  const approvePolicy = async (p) => {
    // Old (active) vs new summary before activation — mandatory change reason follows.
    const active = state.items.find((x) => x.product === p.product && x.status === "active");
    let summary = <div data-testid="activation-summary" className="mb-3 text-[12px] text-amber-200">No active policy for this product yet — this version becomes the first active policy.</div>;
    if (active) {
      try {
        const d = await AdminCreditPolicies.diff(active.id, p.id);
        const changes = d.changes || d.diff || d.fields || [];
        const list = Array.isArray(changes) ? changes : Object.entries(changes).map(([field, v]) => ({ field, ...(typeof v === "object" ? v : { to: v }) }));
        summary = (
          <div data-testid="activation-summary" className="mb-3 rounded-lg border border-white/10 p-3 text-[12px] text-white/85 max-h-48 overflow-auto">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-1">Old v{active.version} → New v{p.version} · {list.length} change(s)</div>
            {list.length === 0 && <div className="text-white/60">No parameter differences detected.</div>}
            {list.map((c, i) => <div key={c.field || i}><span className="text-[#F0D788]">{c.field || c.path}</span>: {String(c.from ?? c.old ?? c.a ?? "—")} → <strong>{String(c.to ?? c.new ?? c.b ?? "—")}</strong></div>)}
          </div>
        );
      } catch { summary = <div data-testid="activation-summary" className="mb-3 text-[12px] text-rose-200">Could not load old-vs-new diff — review the Diff view before activating.</div>; }
    }
    doTransition(`Approve & activate v${p.version} · ${p.product}`, (reason) => AdminCreditPolicies.approve(p.id, reason, p.canonical_input_hash), summary);
  };
  const rejectPolicy = (id) => doTransition("Reject policy",
    (reason) => AdminCreditPolicies.reject(id, reason));
  const returnPolicy = (id) => doTransition("Return to draft",
    (reason) => AdminCreditPolicies.returnToDraft(id, reason));
  const retirePolicy = (id) => doTransition("Retire active policy",
    (reason) => AdminCreditPolicies.retire(id, reason));

  const cloneFromActive = (product) => doTransition(
    `Clone from active · ${product} → new draft version`,
    (reason) => AdminCreditPolicies.cloneFromActive(product, reason));

  return (
    <div className="min-h-screen bg-[#0b0f1a] p-4 text-neutral-200 sm:p-6">
      <div className="mx-auto max-w-6xl space-y-4">
        <div className="rounded-lg border border-amber-400/60 bg-amber-500/10 px-4 py-3 text-center text-xs font-bold uppercase tracking-widest text-amber-200">
          CENTRAL CREDIT POLICY ENGINE — ACTIVE PARAMETERS FEED THE LOCKED MASTER v1.0 LETTER ENGINE (policy version snapshotted on every letter)
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-white">Credit Policies · V2</h1>
            <p className="text-xs text-neutral-500">
              Maker-checker workflow · draft → pending → active → retired.
              Approver must differ from every editor.
            </p>
          </div>
          <div className="flex gap-2">
            <button data-testid="policy-new-draft"
                    onClick={() => setEditing("new")}
                    className="rounded border border-amber-400/60 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-200 hover:bg-amber-500/20">
              + New Draft
            </button>
            <Link to="/admin/eligibility-checks" data-testid="policy-back-link"
                  className="rounded border border-white/10 px-3 py-1 text-xs text-neutral-300 hover:bg-white/5">
              ← Eligibility Checks
            </Link>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 text-[11px]">
          <span className="rounded-full border border-white/10 bg-neutral-900 px-2 py-0.5 text-neutral-400">
            ELIGIBILITY_V2_ENABLED = {String(state.flags?.eligibility_v2_enabled)}
          </span>
          <span className="rounded-full border border-white/10 bg-neutral-900 px-2 py-0.5 text-neutral-400">
            SECURITY_BACKED_ROUTE_ENABLED = {String(state.flags?.security_backed_route_enabled)}
          </span>
        </div>

        {state.loading && <div data-testid="policy-loading" className="p-4 text-neutral-400">Loading…</div>}
        {state.error && (
          <div data-testid="policy-error"
               className="rounded border border-rose-500/40 bg-rose-950/40 p-4 text-rose-200">
            {state.error}
          </div>
        )}

        {!state.loading && !state.error && <PolicyControlCenter policies={state.items} />}
        {!state.loading && !state.error && (
          <div className="overflow-x-auto rounded-xl border border-white/10 bg-neutral-950/60">
            {state.items.length === 0 ? (
              <p data-testid="policy-empty" className="p-6 text-center text-sm italic text-neutral-500">
                No policies yet — click <b>+ New Draft</b> to create the first one.
              </p>
            ) : (
              <table className="w-full min-w-[900px] text-sm">
                <thead className="border-b border-white/10 bg-neutral-900 text-left text-[11px] uppercase tracking-wider text-neutral-400">
                  <tr>
                    <th className="px-3 py-2">Product</th>
                    <th className="px-3 py-2">Version</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Min Income</th>
                    <th className="px-3 py-2">Max FOIR</th>
                    <th className="px-3 py-2">Multiplier</th>
                    <th className="px-3 py-2">Product Cap</th>
                    <th className="px-3 py-2">Created</th>
                    <th className="px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {state.items.map((p) => (
                    <tr key={p.id} data-testid={`policy-row-${p.id}`}
                        className="border-b border-white/5 hover:bg-white/5">
                      <td className="px-3 py-2 text-neutral-100">{p.product.replace(/_/g, " ")}</td>
                      <td className="px-3 py-2 font-mono text-neutral-300">v{p.version}</td>
                      <td className="px-3 py-2"><StatusChip status={p.status} /></td>
                      <td className="px-3 py-2 text-neutral-300">₹{p.min_verified_monthly_income}</td>
                      <td className="px-3 py-2 text-neutral-300">{p.max_foir}</td>
                      <td className="px-3 py-2 text-neutral-300">{p.eligibility_multiplier}</td>
                      <td className="px-3 py-2 text-neutral-300">{p.product_cap ? `₹${p.product_cap}` : "—"}</td>
                      <td className="px-3 py-2 text-[10px] text-neutral-500">
                        {p.created_by} · {p.created_at?.slice(0, 10)}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {p.status === "draft" && (
                            <>
                              <button data-testid={`policy-edit-${p.id}`}
                                      onClick={() => setEditing(p)}
                                      className="rounded border border-white/10 px-2 py-0.5 text-[10px] text-neutral-200 hover:bg-white/5">
                                Edit
                              </button>
                              <button data-testid={`policy-submit-${p.id}`}
                                      onClick={() => submitPolicy(p.id)}
                                      className="rounded border border-amber-400/40 px-2 py-0.5 text-[10px] text-amber-200 hover:bg-amber-400/10">
                                Submit
                              </button>
                            </>
                          )}
                          {p.status === "pending_committee_approval" && (
                            <>
                              <button data-testid={`policy-approve-${p.id}`}
                                      onClick={() => approvePolicy(p)}
                                      className="rounded border border-emerald-400/40 px-2 py-0.5 text-[10px] text-emerald-200 hover:bg-emerald-400/10">
                                Approve
                              </button>
                              <button data-testid={`policy-reject-${p.id}`}
                                      onClick={() => rejectPolicy(p.id)}
                                      className="rounded border border-rose-400/40 px-2 py-0.5 text-[10px] text-rose-200 hover:bg-rose-400/10">
                                Reject
                              </button>
                              <button data-testid={`policy-return-${p.id}`}
                                      onClick={() => returnPolicy(p.id)}
                                      className="rounded border border-white/10 px-2 py-0.5 text-[10px] text-neutral-200 hover:bg-white/5">
                                Return to Draft
                              </button>
                            </>
                          )}
                          {p.status === "active" && (
                            <>
                              <button data-testid={`policy-clone-${p.id}`}
                                      onClick={() => cloneFromActive(p.product)}
                                      className="rounded border border-amber-400/40 px-2 py-0.5 text-[10px] text-amber-200 hover:bg-amber-400/10">
                                Clone as v{p.version + 1}
                              </button>
                              <button data-testid={`policy-retire-${p.id}`}
                                      onClick={() => retirePolicy(p.id)}
                                      className="rounded border border-white/10 px-2 py-0.5 text-[10px] text-neutral-200 hover:bg-white/5">
                                Retire
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {editing && (
        <DraftEditor
          policy={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
      {prompt && (
        <ReasonPrompt title={prompt.title} summary={prompt.summary}
                      onCancel={() => setPrompt(null)}
                      onConfirm={prompt.onConfirm}
                      busy={busy} />
      )}
    </div>
  );
}
